/**
 * MOLR interactions
 * -----------------
 * The behavioural half of the enhancement pass. Every module here is a
 * progressive enhancement: if this file fails to load, the site still works
 * (native <details>, Horizon's own cart/gallery behaviour, server-side form
 * validation). Nothing here is load-bearing.
 *
 * Shared rules, honoured by every module:
 *   - Skipped inside the theme editor (design mode re-renders sections and
 *     fights any state we hold).
 *   - prefers-reduced-motion: interactions still WORK, but the motion is
 *     dropped — numbers set instantly, panels open without easing. Checked
 *     live so a mid-session OS change is respected.
 *   - Styles live in assets/molr.css §21–§28. This file only toggles classes
 *     and, where a value must be measured at runtime (accordion height,
 *     counting numbers), writes inline values.
 *
 * Modules:
 *   1. Cart count-up      — subtotal ticks to its new value
 *   2. Cart bubble pulse  — the header count answers an add-to-cart
 *   3. Quantity stepper   — the number lifts when a tap registers
 *   4. FAQ accordion      — native <details>, eased open/close
 *   5. Progression orbit  — hover/focus a stage to preview it as current
 *   6. Forms              — inline validation + submit/confirm states
 */
(() => {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReduced = () => reduce.matches;

  /* ======================================================================
     Utilities
     ====================================================================== */

  /**
   * Pull the numeric value out of a formatted money string, and remember how
   * it was formatted so we can rebuild interpolated values identically.
   * "$1,240.00" → { value: 1240, decimals: 2, group: ',', decimal: '.',
   *                 prefix: '$', suffix: '' }
   */
  function parseMoney(text) {
    if (typeof text !== 'string') return null;
    const match = text.match(/[\d][\d.,\s]*\d|\d/);
    if (!match) return null;
    const numStr = match[0];
    const prefix = text.slice(0, match.index);
    const suffix = text.slice(match.index + numStr.length);

    // Work out which separator is the decimal one: the last '.' or ',' that is
    // followed by 2 (or fewer) trailing digits is the decimal separator.
    let decimal = '';
    let group = '';
    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');
    const lastSep = Math.max(lastDot, lastComma);
    let decimals = 0;
    if (lastSep !== -1) {
      const trailing = numStr.length - lastSep - 1;
      if (trailing > 0 && trailing <= 2) {
        decimal = numStr[lastSep];
        decimals = trailing;
        group = decimal === '.' ? ',' : '.';
      } else {
        group = numStr[lastSep];
      }
    }

    let normalized = numStr;
    if (group) normalized = normalized.split(group).join('');
    if (decimal && decimal !== '.') normalized = normalized.replace(decimal, '.');
    const value = parseFloat(normalized.replace(/\s/g, ''));
    if (Number.isNaN(value)) return null;
    return { value, decimals, group, decimal: decimal || '.', prefix, suffix };
  }

  /** Rebuild a money string for an interpolated value using the same format. */
  function formatMoney(value, fmt) {
    let s = value.toFixed(fmt.decimals);
    if (fmt.decimals > 0 && fmt.decimal !== '.') s = s.replace('.', fmt.decimal);
    if (fmt.group) {
      const parts = s.split(fmt.decimal);
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, fmt.group);
      s = parts.join(fmt.decimal);
    }
    return fmt.prefix + s + fmt.suffix;
  }

  /** Ease-out cubic — matches the CSS --molr-ease-out feel for JS-driven counts. */
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  /* ======================================================================
     1 + 2. Cart count-up and bubble pulse
     Both react to the cart mutating. Horizon re-renders cart markup in place
     (morph), so the text nodes we watch persist and only their contents
     change — a subtree MutationObserver catches every update, whether from
     the drawer steppers or an add-to-cart anywhere on the page.
     ====================================================================== */

  const CartMotion = (() => {
    let lastBubbleCount = null;
    // The last STABLE subtotal string. Kept at module scope (not on the node)
    // because Horizon re-renders the cart via morph, which can REPLACE the
    // subtotal element entirely — a per-node observer would die with it. The
    // high-level observer below reconciles against this stored value instead,
    // so a swapped-in node still animates from the previous total.
    let lastSubtotal = null;
    const counting = new WeakSet();

    function countUp(el, fromText, toText) {
      const from = parseMoney(fromText);
      const to = parseMoney(toText);
      // If either side isn't parseable (e.g. "Free"), just let the new text stand.
      if (!from || !to || from.value === to.value) return;

      counting.add(el);
      const duration = 520;
      const start = performance.now();
      el.classList.add('molr-counting');

      function frame(now) {
        const t = Math.min(1, (now - start) / duration);
        const v = from.value + (to.value - from.value) * easeOut(t);
        el.textContent = formatMoney(v, to);
        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          el.textContent = toText; // land exactly on the real value
          el.classList.remove('molr-counting');
          counting.delete(el);
        }
      }
      requestAnimationFrame(frame);
    }

    // Reconcile the subtotal against the stored value. Survives node
    // replacement because it re-queries every time and animates whatever
    // element is currently present from the last stable total to the new one.
    function reconcileSubtotal() {
      const el = document.querySelector('[data-cart-subtotal]');
      if (!el) return;
      if (counting.has(el)) return; // mid-animation — its own writes; skip
      const next = el.textContent.trim();
      if (!next) return;
      if (lastSubtotal === null) {
        lastSubtotal = next; // first sighting — nothing to animate from
        return;
      }
      if (next === lastSubtotal) return;
      const from = lastSubtotal;
      lastSubtotal = next;
      if (!prefersReduced()) countUp(el, from, next);
    }

    function pulseBubble() {
      const count = document.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
      const bubble = document.querySelector('.cart-bubble');
      if (!count || !bubble) return;
      const value = parseInt(count.textContent.trim(), 10);
      if (Number.isNaN(value)) return;
      if (lastBubbleCount !== null && value > lastBubbleCount && !prefersReduced()) {
        bubble.classList.remove('molr-bubble-pulse');
        // Force reflow so the animation can restart on a repeated add.
        void bubble.offsetWidth;
        bubble.classList.add('molr-bubble-pulse');
        bubble.addEventListener(
          'animationend',
          () => bubble.classList.remove('molr-bubble-pulse'),
          { once: true }
        );
      }
      lastBubbleCount = value;
    }

    function init() {
      // Seed the starting count so the first genuine add is what pulses.
      const seed = document.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
      if (seed) {
        const v = parseInt(seed.textContent.trim(), 10);
        lastBubbleCount = Number.isNaN(v) ? 0 : v;
      }

      reconcileSubtotal(); // seed the starting subtotal

      // One high-level observer reconciles the subtotal count-up and the bubble
      // pulse on any cart-related DOM change (add-to-cart, stepper, remove) —
      // including full drawer re-renders that swap the nodes out. We observe
      // only childList/subtree (not attributes), so Lenis's transform writes
      // and scroll-reveal class toggles never wake it; a rAF coalesces bursts.
      const root = document.querySelector('.page-wrapper') || document.body;
      let scheduled = false;
      const hi = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          reconcileSubtotal();
          pulseBubble();
        });
      });
      hi.observe(root, { childList: true, subtree: true });
    }

    return { init };
  })();

  /* ======================================================================
     3. Quantity stepper — the number lifts when a tap registers
     The press itself is CSS (:active scale). This adds the value "lift" once
     a stepper button is used, so the changed number reads as changed. Bound
     by delegation so it survives cart re-renders.
     ====================================================================== */

  function initQuantitySteppers() {
    document.addEventListener('click', (event) => {
      const btn =
        event.target instanceof Element &&
        event.target.closest('.quantity-plus, .quantity-minus');
      if (!btn) return;
      if (prefersReduced()) return;
      const selector = btn.closest('.quantity-selector, quantity-selector-component, cart-quantity-selector-component');
      const input = selector && selector.querySelector('input');
      if (!input) return;
      // Let Horizon apply the new value first, then lift it.
      window.setTimeout(() => {
        input.classList.remove('molr-qty-bumped');
        void input.offsetWidth;
        input.classList.add('molr-qty-bumped');
        input.addEventListener(
          'animationend',
          () => input.classList.remove('molr-qty-bumped'),
          { once: true }
        );
      }, 60);
    });
  }

  /* ======================================================================
     4. FAQ accordion — native <details>, eased open/close
     Keeps <details> as the source of truth (and the no-JS fallback). We just
     intercept the toggle to animate the answer's height, then hand state back
     to the element.
     ====================================================================== */

  function initFaqAccordions() {
    const items = document.querySelectorAll('.molr-faq__item');
    items.forEach((item) => {
      const summary = item.querySelector('summary');
      const answer = item.querySelector('.molr-faq__answer');
      if (!summary || !answer) return;
      item.classList.add('molr-faq--animated');

      summary.addEventListener('click', (event) => {
        if (prefersReduced()) return; // native snap is fine for reduced-motion
        event.preventDefault();
        if (item.dataset.molrAnimating) return;
        item.dataset.molrAnimating = 'true';

        const isOpen = item.hasAttribute('open');
        if (!isOpen) {
          // OPEN: reveal the element, animate 0 → content height, then release.
          item.setAttribute('open', '');
          const target = answer.scrollHeight;
          answer.style.height = '0px';
          answer.style.opacity = '0';
          requestAnimationFrame(() => {
            answer.style.height = target + 'px';
            answer.style.opacity = '1';
          });
          answer.addEventListener(
            'transitionend',
            function done(e) {
              if (e.propertyName !== 'height') return;
              answer.style.height = '';
              answer.style.opacity = '';
              answer.removeEventListener('transitionend', done);
              delete item.dataset.molrAnimating;
            },
            { once: false }
          );
        } else {
          // CLOSE: animate current height → 0, then drop the open attribute.
          const current = answer.scrollHeight;
          answer.style.height = current + 'px';
          answer.style.opacity = '1';
          requestAnimationFrame(() => {
            answer.style.height = '0px';
            answer.style.opacity = '0';
          });
          answer.addEventListener(
            'transitionend',
            function done(e) {
              if (e.propertyName !== 'height') return;
              item.removeAttribute('open');
              answer.style.height = '';
              answer.style.opacity = '';
              answer.removeEventListener('transitionend', done);
              delete item.dataset.molrAnimating;
            },
            { once: false }
          );
        }
      });
    });
  }

  /* ======================================================================
     5. Progression orbit — preview any stage as the live one
     The diagram marks one "current" stage. Hovering or focusing a node
     previews IT as current; only one node is ever active, so The Ember
     Rationing Rule holds. Leaving the ring restores the built-in default.
     Nodes are made keyboard-focusable so this is not mouse-only.
     ====================================================================== */

  function initProgressionOrbit() {
    const rings = document.querySelectorAll('.molr-progression__ring');
    rings.forEach((ring) => {
      const nodes = Array.from(ring.querySelectorAll('.molr-progression__node'));
      if (nodes.length < 2) return;

      nodes.forEach((node) => {
        node.setAttribute('tabindex', '0');

        const activate = () => {
          ring.setAttribute('data-molr-exploring', '');
          nodes.forEach((n) => n.classList.toggle('molr-progression__node--active', n === node));
        };
        const clear = () => {
          // Only clear when focus/hover has genuinely left the whole ring.
          if (ring.matches(':hover') || ring.contains(document.activeElement)) return;
          ring.removeAttribute('data-molr-exploring');
          nodes.forEach((n) => n.classList.remove('molr-progression__node--active'));
        };

        node.addEventListener('pointerenter', activate);
        node.addEventListener('focus', activate);
        node.addEventListener('pointerleave', () => window.setTimeout(clear, 10));
        node.addEventListener('blur', () => window.setTimeout(clear, 10));
      });
    });
  }

  /* ======================================================================
     6. Forms — inline validation + submit/confirm states
     Applies to the app-signup and any newsletter form carrying an email
     field. The server form still does the real work (and, for the customer
     form, renders the authoritative success message on reload); this adds the
     smooth pre-submit feedback the round-trip can't.
     No green/red — clay for "check this", ember for confirmed (see §27).
     ====================================================================== */

  function initForms() {
    const forms = document.querySelectorAll('.molr-app__form, form.molr-newsletter, [data-molr-form]');
    forms.forEach((form) => {
      const email = form.querySelector('input[type="email"]');
      if (!email) return;

      // A feedback line, inserted once, that we slide in/out.
      let msg = form.querySelector('.molr-field__msg');
      if (!msg) {
        msg = document.createElement('span');
        msg.className = 'molr-field__msg';
        msg.setAttribute('role', 'alert');
        msg.setAttribute('aria-live', 'polite');
        email.insertAdjacentElement('afterend', msg);
      }

      const showMsg = (text) => {
        msg.textContent = text;
        msg.setAttribute('data-show', 'true');
        email.classList.add('molr-invalid');
        email.setAttribute('aria-invalid', 'true');
      };
      const clearMsg = () => {
        msg.setAttribute('data-show', 'false');
        email.classList.remove('molr-invalid');
        email.removeAttribute('aria-invalid');
      };

      const valid = () =>
        email.value.trim() !== '' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.value.trim());

      const messageFor = () =>
        email.value.trim() === ''
          ? 'Add your email and we’ll let you know.'
          : 'That email address doesn’t look complete — check for a typo.';

      // Clear the warning as soon as the visitor starts fixing it.
      email.addEventListener('input', () => {
        if (email.classList.contains('molr-invalid') && valid()) clearMsg();
      });
      // Validate when they leave the field, but never nag an empty field.
      email.addEventListener('blur', () => {
        if (email.value.trim() !== '' && !valid()) showMsg(messageFor());
      });

      // The field carries native constraints (type=email, required), so the
      // browser fires `invalid` and cancels submit before our submit handler
      // runs. Intercept it: suppress the native bubble and show our own warm
      // clay feedback instead, keeping the accessible semantics.
      email.addEventListener('invalid', (event) => {
        event.preventDefault();
        showMsg(messageFor());
        email.focus();
      });

      form.addEventListener('submit', (event) => {
        if (!valid()) {
          // Belt-and-braces for forms marked novalidate: the invalid event
          // won't fire there, so validate here too.
          event.preventDefault();
          showMsg(messageFor());
          email.focus();
          return;
        }
        clearMsg();
        // Enter the submitting state. The customer form reloads the page, so
        // this shows briefly before the server confirmation renders.
        const btn = form.querySelector('button[type="submit"], .molr-btn');
        if (btn) btn.classList.add('molr-submitting');
      });
    });

    // Server already rendered a success note (customer form reload). Dress it
    // with the confirmation check + reveal animation if present.
    document.querySelectorAll('.molr-app__note[role="status"]').forEach((note) => {
      if (note.dataset.molrDressed) return;
      note.dataset.molrDressed = 'true';
      const wrap = note.closest('[data-molr-reveal-item]') || note.parentElement;
      if (wrap) wrap.classList.add('molr-form-confirmed');
      note.classList.add('molr-form__success');
      const check = document.createElement('span');
      check.className = 'molr-form__success-check';
      check.setAttribute('aria-hidden', 'true');
      check.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      note.insertBefore(check, note.firstChild);
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  function init() {
    CartMotion.init();
    initQuantitySteppers();
    initFaqAccordions();
    initProgressionOrbit();
    initForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
